import React from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Container,
  Flex,
  Heading,
  Text,
  Tag,
  HStack,
  Stack,
  Divider,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
  useColorModeValue,
} from "@chakra-ui/react";

import ImageSlider from "./ImageSlider";

/**
 * ProgramAll.jsx (Professional TRAVERSAL style)
 * - NO content changes: all text/images/links are kept as-is.
 * - Single file (no extra components imported except your existing ImageSlider).
 * - Removes Aphrodite + <hr/> and replaces with modern Chakra layout.
 * - Matches the “SITE-CAV program” vibe: hero header + clean section cards.
 */

export default function TrainingTrackMerged() {
  const isMobile = useSelector((state) => state.windowSize.isMobile);

  return (
    <Box>
      <TraversalProgramHeader />

      <ProgramSection title="TRAINING">
        <TrainingSection isMobile={isMobile} />
      </ProgramSection>

      <ProgramSection title="HACKATHON">
        <HackathonSection isMobile={isMobile} />
      </ProgramSection>

      <ProgramSection title="INTERNSHIPS">
        <InternshipSection isMobile={isMobile} />
      </ProgramSection>

      <ProgramSection title="INTERNATIONAL EXCHANGES">
        <InternationalExchangesSection isMobile={isMobile} />
      </ProgramSection>

      <ProgramSection title="CONFERENCES & HOW TO APPLY">
        <ConferencesAndApplySection isMobile={isMobile} />
      </ProgramSection>

      <Box pb={{ base: 10, md: 14 }} />
    </Box>
  );
}

/* =========================================================
   HEADER (like SITE-CAV, TRAVERSAL colors)
========================================================= */
function TraversalProgramHeader() {
  const accent = "#06B6D4";
  const accent2 = "#22D3EE";

  const border = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");
  const titleColor = useColorModeValue("#0F172A", "white");
  const subColor = useColorModeValue("gray.700", "gray.300");

  const heroBg = useColorModeValue(
    "radial-gradient(1000px 420px at 12% 0%, rgba(6,182,212,0.18), transparent 60%), radial-gradient(900px 380px at 88% 25%, rgba(34,211,238,0.14), transparent 60%), linear-gradient(180deg, rgba(2,6,23,0.02), rgba(2,6,23,0.00))",
    "radial-gradient(1000px 420px at 12% 0%, rgba(6,182,212,0.20), transparent 60%), radial-gradient(900px 380px at 88% 25%, rgba(34,211,238,0.14), transparent 60%), linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.00))"
  );

  return (
    <Box
      pt={{ base: 7, md: 10 }}
      pb={{ base: 8, md: 10 }}
      bg={heroBg}
      borderBottom="1px solid"
      borderColor={border}
    >
      <Container maxW="1200px">
        <HStack spacing={2} flexWrap="wrap">
          <Tag
            size="lg"
            borderRadius="full"
            bg={useColorModeValue("white", "rgba(255,255,255,0.06)")}
            border="1px solid"
            borderColor={border}
            fontWeight="800"
          >
            University of Ottawa
          </Tag>

          <Tag
            size="lg"
            borderRadius="full"
            bg={useColorModeValue("rgba(6,182,212,0.12)", "rgba(6,182,212,0.18)")}
            color={useColorModeValue("#0E7490", "#67E8F9")}
            fontWeight="900"
          >
            TRAVERSAL
          </Tag>

        </HStack>

        <Heading
          mt={4}
          fontSize={{ base: "3xl", md: "4xl" }}
          color={titleColor}
          letterSpacing="tight"
          lineHeight={1.05}
        >
          <Box as="span" bgClip="text" bgGradient={`linear(to-r, ${accent}, ${accent2})`}>
            TRAVERSAL
          </Box>{" "}
          Program
        </Heading>

        <Text mt={3} color={subColor} fontSize={{ base: "md", md: "lg" }} maxW="90ch" lineHeight={1.8}>
          Training, hackathons, internships, international exchanges, conferences, and application guidance for CREATE
          trainees.
        </Text>
      </Container>
    </Box>
  );
}

/* =========================================================
   SECTION SHELL (consistent rhythm)
========================================================= */
function ProgramSection({ title, children }) {
  const border = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");
  const titleColor = useColorModeValue("#0F172A", "white");
  const bg = useColorModeValue("white", "#0B1220");

  const accent = "#06B6D4";
  const accent2 = "#22D3EE";

  return (
    <Box bg={bg}>
      <Container maxW="1200px" py={{ base: 4, md: 6 }}>
        <Stack spacing={3} textAlign={{ base: "center", md: "left" }}>
          <Heading fontSize={{ base: "2xl", md: "3xl" }} color={titleColor} letterSpacing="tight" fontWeight="900">
            {title}
          </Heading>

          <Box
            mx={{ base: "auto", md: 0 }}
            w={{ base: "110px", md: "140px" }}
            h="3px"
            borderRadius="999px"
            bg={`linear-gradient(90deg, ${accent} 0%, ${accent2} 45%, rgba(34,211,238,0) 100%)`}
          />
        </Stack>

        <Box mt={{ base: 7, md: 9 }}>
          <Box
            border="1px solid"
            borderColor={border}
            borderRadius="26px"
            bg={useColorModeValue("rgba(255,255,255,0.85)", "rgba(255,255,255,0.03)")}
            boxShadow={useColorModeValue("0 14px 44px rgba(0,0,0,0.08)", "none")}
            p={{ base: 5, md: 7 }}
          >
            {children}
          </Box>
        </Box>

        <Box mt={{ base: 10, md: 12 }}>
          <Divider borderColor={border} />
        </Box>
      </Container>
    </Box>
  );
}

/* =========================================================
   ACCORDION STYLING HELPERS
========================================================= */
function ProgramAccordion({ children, defaultIndex = [0] }) {
  const border = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");
  return (
    <Accordion allowToggle allowMultiple width="100%" defaultIndex={defaultIndex}>
      {React.Children.map(children, (child, idx) => (
        <Box
          key={idx}
          border="1px solid"
          borderColor={border}
          borderRadius="18px"
          overflow="hidden"
          mb={4}
        >
          {child}
        </Box>
      ))}
    </Accordion>
  );
}

function ProgramAccordionItem({ title, children, isMobile, fontSize = 18 }) {
  const titleColor = useColorModeValue("#0F172A", "white");
  const panelColor = useColorModeValue("gray.700", "gray.300");
  const buttonBg = useColorModeValue("rgba(2,6,23,0.02)", "rgba(255,255,255,0.04)");

  return (
    <AccordionItem border="none">
      <AccordionButton
        bg={buttonBg}
        _hover={{ bg: useColorModeValue("rgba(2,6,23,0.04)", "rgba(255,255,255,0.06)") }}
        py={4}
      >
        <Box as="span" flex="1" textAlign="left" fontSize={fontSize} fontWeight="900" color={titleColor}>
          {title}
        </Box>
        <AccordionIcon />
      </AccordionButton>

      <AccordionPanel pb={5} pt={4} fontSize={{ base: 15, md: 16 }} color={panelColor} textAlign={isMobile ? "center" : "left"}>
        {children}
      </AccordionPanel>
    </AccordionItem>
  );
}

/* =========================================================
   CONTENT SECTIONS (same text as your file)
========================================================= */
function TrainingSection({ isMobile }) {
  return (
    <Box>
      <ProgramAccordion defaultIndex={[0]}>
        <ProgramAccordionItem
          title="Equity Diversity and Inclusion (EDI) Workshops"
          isMobile={isMobile}
          fontSize={20}
        >
          Each year, CREATE TRAVERSAL provides EDI training sessions. EDI training is mandatory for all CREATE trainees.
          The training includes both synchronous and asynchronous modules.
          <Box mt={5}>
            <ImageSlider
              images={["N-EDI 2023.jpg", "N-EDI en 2024.jpg", "N-EDI en 2026.jpg"]}
            />
          </Box>
        </ProgramAccordionItem>

        <ProgramAccordionItem
          title="AV-UAV Systems Survey & Tutorial Workshops"
          isMobile={isMobile}
          fontSize={20}
        >
          <b>The AV-UAV Systems Survey &amp; Tutorial Workshops</b> are a vital component of the proposed program.
          These workshops are designed to provide trainees with comprehensive training in producing surveys and tutorials
          on state-of-the-art topics within the field of Autonomous Vehicle-Uncrewed Aerial Vehicle (AV-UAV) systems.
          The goal is to equip trainees with the skills necessary to explore and understand the latest developments,
          challenges, and opportunities in their respective research areas.
          <Box mt={5}>
            <ImageSlider
              images={[
                "N-SurveyandTutorials_en 2024.jpg",
                "N-SurveyandTutorials_en 2025.png",
                "N-SurveyandTutorials_en 2026.jpg",
              ]}
            />
          </Box>
        </ProgramAccordionItem>

        <ProgramAccordionItem title="New graduate-level course stream" isMobile={isMobile} fontSize={20}>
          <b>The CREATE TRAVERSAL program</b> is set to introduce a range of cutting-edge graduate-level courses.
          These courses, offered both on-campus and through distance learning, cover critical areas in autonomous systems.
        </ProgramAccordionItem>

        <ProgramAccordionItem title="Professional Skills & Training" isMobile={isMobile} fontSize={20}>
          <b>The Annual TRAVERSAL Workshop</b> is a key event where trainees engage with industry and academic experts
          through talks, panels, and research showcases. This gathering fosters networking opportunities and explores
          themes aligned with the program&apos;s scope.
          <Box mt={5}>
            <ImageSlider images={["N-hackathon 2024.jpg", "N-hackathon 2025.jpg"]} />
          </Box>
        </ProgramAccordionItem>
      </ProgramAccordion>
    </Box>
  );
}

function HackathonSection({ isMobile }) {
  const textColor = useColorModeValue("gray.700", "gray.300");

  return (
    <Box>
      <Text color={textColor} fontSize={{ base: 15, md: 16 }} lineHeight={1.9} textAlign={isMobile ? "center" : "left"}>
        The TRAVERSAL Hackathon is an annual event that brings together second-year graduate students from the program.
        Co-chaired by two co-applicants, this intensive 48-hour event challenges students to collaborate in diverse teams
        and tackle research problems related to autonomous decision-making for AV-UAVs.
      </Text>

      <Box mt={6}>
        <ImageSlider images={["N-hackathon 2024.jpg", "N-hackathon 2025.jpg"]} />
      </Box>
    </Box>
  );
}

function InternshipSection({ isMobile }) {
  const textColor = useColorModeValue("gray.700", "gray.300");

  return (
    <Box>
      <Text color={textColor} fontSize={{ base: 15, md: 16 }} lineHeight={1.9} textAlign={isMobile ? "center" : "left"}>
        Trainees will have the opportunity to embark on an industrial internship program that delves into various facets
        of our program in collaboration with esteemed industry partners. These internships include:
      </Text>

      <Box mt={6}>
        <ProgramAccordion defaultIndex={[]}>
          <ProgramAccordionItem title="Trustworthy and Responsible AI Internships" isMobile={isMobile}>
            Delve into the ethical and responsible dimensions of AI during your internship. Gain practical experience in
            ensuring that AI systems are built with trustworthiness and ethical considerations in mind.
          </ProgramAccordionItem>

          <ProgramAccordionItem title="Cybersecurity and Cyber-Physical Training" isMobile={isMobile}>
            Join our industry partners for specialized training in cybersecurity and cyber-physical aspects. Develop
            expertise in safeguarding digital and physical systems from threats and vulnerabilities.
          </ProgramAccordionItem>

          <ProgramAccordionItem title="Communication, Sensing, and Human Factors Training" isMobile={isMobile}>
            Enhance your skills in communication, sensor technology, and human factors by participating in training
            sessions offered by our industry partners. Explore the essential components of effective communication and
            human-centred design principles.
          </ProgramAccordionItem>
        </ProgramAccordion>
      </Box>
    </Box>
  );
}

function InternationalExchangesSection({ isMobile }) {
  const textColor = useColorModeValue("gray.700", "gray.300");

  return (
    <Box>
      <Text color={textColor} fontSize={{ base: 15, md: 16 }} lineHeight={1.9} textAlign={isMobile ? "center" : "left"}>
        <b>Masters and PhD trainees</b> enrolled in our program will have the exceptional opportunity to participate in
        an international exchange program, which will be awarded on a competitive basis. This initiative is designed to
        provide our trainees with a unique and enriching experience that extends beyond the boundaries of the trainee.
        <p />
        <br />
        <b>The TRAVERSAL program</b> extends its reach globally through an International Exchange initiative. In addition
        to industry internships, trainees have the chance to engage in research projects and collaborations with partner
        institutions in the United States, Brazil, Germany, the United Kingdom, and France. This exchange program
        enriches their perspectives, fosters international research exposure, and promotes collaboration on
        cutting-edge projects. We also offer virtual internships to adapt to evolving travel conditions. All
        opportunities are provided on a competitive and equitable basis, overseen by institutional Equity, Diversity,
        and Inclusion (EDI) Advisors.
      </Text>
    </Box>
  );
}

function ConferencesAndApplySection({ isMobile }) {
  const textColor = useColorModeValue("gray.700", "gray.300");
  const border = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");

  return (
    <Box>
      {/* CONFERENCES */}
      <Text color={textColor} fontSize={{ base: 15, md: 16 }} lineHeight={1.9} textAlign={isMobile ? "center" : "left"}>
        The conferences organized within the TRAVERSAL program are designed to advance the program&apos;s goals and
        objectives. These events bring together leading experts, researchers, and professionals in the fields of
        communication, artificial intelligence (AI), physics, and operations research, all with a common aim: to drive
        innovation and excellence within the program.
      </Text>

      <Box mt={6}>
        <ImageSlider
          images={["N-conference 1.png", "N-conference 2.png"]}
          links={[
            "https://wfiot2024.iot.ieee.org/workshop-transformative-results-autonomous-vehicles-reliable-services-air-and-land-traversal",
            "https://hpsr2026.ieee-hpsr.org/workshop-network-architectures-and-intelligence-smart-mobility-and-autonomous-systems-traversal",
          ]}
        />
      </Box>

      <Box mt={{ base: 8, md: 10 }}>
        <Divider borderColor={border} />
      </Box>

      {/* HOW TO APPLY */}
      <Heading mt={{ base: 8, md: 10 }} fontSize={{ base: "xl", md: "2xl" }} letterSpacing="tight">
        HOW TO APPLY
      </Heading>

      <Box mt={6}>
        <ProgramAccordion defaultIndex={[]}>
          <ProgramAccordionItem title="1. Prepare Your Application Documents" isMobile={isMobile}>
            <b>Curriculum Vitae (CV):</b> Create a comprehensive CV that highlights your educational background, research
            experience, academic achievements, and any relevant professional experience.
            <br />
            <br />
            <b>Educational Transcripts:</b> Gather your educational transcripts, including undergraduate and, if
            applicable, postgraduate degrees. Ensure that these documents clearly show your academic history, grades,
            and qualifications.
            <br />
            <br />
            <b>Recent Published Works:</b> Include recent published works, such as research papers, articles, or
            projects, that showcase your academic and research capabilities. Make sure to provide the name(s) of your
            supervisor(s) for context.
          </ProgramAccordionItem>

          <ProgramAccordionItem title="2. Compile Your Application Package" isMobile={isMobile}>
            Combine all the required documents into a single electronic package, preferably in PDF format, to facilitate
            efficient processing.
          </ProgramAccordionItem>

          <ProgramAccordionItem title="3. Email Your Application" isMobile={isMobile}>
            Send your complete application package to our dedicated email address:
            <br />
            <b>Email Address: scvic-civic@uottawa.ca</b>
          </ProgramAccordionItem>

          <ProgramAccordionItem title="4. Subject Line" isMobile={isMobile}>
            In the subject line of your email, please specify &quot;CREATE TRAVERSAL Application - [Your Full
            Name].&quot; This will help us quickly identify and process your application.
          </ProgramAccordionItem>

          <ProgramAccordionItem title="5. Application Deadline" isMobile={isMobile}>
            The trainee program accepts applications throughout the year; however, it&apos;s important to note that
            there is a limited number of slots available annually.
          </ProgramAccordionItem>

          <ProgramAccordionItem title="6. Await Confirmation" isMobile={isMobile}>
            Upon receiving your application, our team will acknowledge its receipt. Please be patient as we review and
            assess all applications.
          </ProgramAccordionItem>

          <ProgramAccordionItem title="7. Interview and Evaluation" isMobile={isMobile}>
            Shortlisted candidates will be contacted for interviews and further evaluation. This is an opportunity for
            us to learn more about your qualifications, research interests, and potential fit within the program.
          </ProgramAccordionItem>
        </ProgramAccordion>
      </Box>

      <Text
        mt={{ base: 7, md: 8 }}
        color={textColor}
        fontSize={{ base: 15, md: 16 }}
        lineHeight={1.9}
        textAlign={isMobile ? "center" : "left"}
      >
        We encourage applications from individuals belonging to the four designated groups: Aboriginal Peoples, Visible
        Minorities, Persons with Disabilities, Women, and Persons from Gender Minorities, as delineated in the equity
        objectives outlined in the Employment Equity Act.
        <br />
        <br />
        We appreciate your interest in the CREATE TRAVERSAL program and look forward to receiving your application. If
        you have any questions or require further assistance during the application process, please do not hesitate to
        contact us. We wish you the best of luck with your application and hope to welcome you into our program soon.
      </Text>
    </Box>
  );
}
